import type { ApiContext } from "@hobbyshopify/backend-client";
import type { AuthHandler } from "trunk.js";
import { createAuthHandler } from "./core/auth/auth-handler";
import type { MerchantSession } from "./core/auth/merchant-session";
import type { BackendApi } from "./core/ops/api";
import { runScan } from "./core/ops/scan-ops";
import { fileToDataUrl } from "./core/primitives/scan-files";
import { ShareLinks } from "./core/primitives/share-links";
import { CapabilitiesRepository } from "./core/repositories/capabilities-repository";
import { CountriesRepository } from "./core/repositories/countries-repository";
import { CustomersRepository } from "./core/repositories/customers-repository";
import { LocationsRepository } from "./core/repositories/locations-repository";
import { PendingCartsRepository } from "./core/repositories/pending-carts-repository";
import { ProfileRepository } from "./core/repositories/profile-repository";
import { ProvidersRepository } from "./core/repositories/providers-repository";
import { ReviewQueueRepository } from "./core/repositories/review-queue-repository";
import { StoreRepository } from "./core/repositories/store-repository";
import { BroadcastStudio } from "./core/services/broadcast-studio";
import { BulkScanQueue } from "./core/services/bulk-scan-queue";
import { StripePayments } from "./core/services/stripe-payments";
import { StripeTerminal } from "./core/services/stripe-terminal";

/**
 * Everything the app is made of, constructed once at startup. Pages declare the
 * subset they need with `Pick<AppDeps, ...>` and receive it through `pick()`.
 */
export interface AppDeps {
  readonly client: BackendApi;
  /** Ops go through `authHandler`; the sign-in ops and the streams read the session directly. */
  readonly session: MerchantSession;
  readonly authHandler: AuthHandler<ApiContext>;
  readonly profileRepository: ProfileRepository;
  readonly storeRepository: StoreRepository;
  readonly locationsRepository: LocationsRepository;
  readonly countriesRepository: CountriesRepository;
  readonly pendingCartsRepository: PendingCartsRepository;
  readonly providersRepository: ProvidersRepository;
  readonly capabilitiesRepository: CapabilitiesRepository;
  readonly reviewQueueRepository: ReviewQueueRepository;
  readonly customersRepository: CustomersRepository;
  readonly bulkScanQueue: BulkScanQueue;
  readonly broadcastStudio: BroadcastStudio;
  readonly stripeTerminal: StripeTerminal;
  readonly stripePayments: StripePayments;
  /** Where the public site shows a shop, stream, break or show: what a share button hands on. */
  readonly shareLinks: ShareLinks;
}

export function createAppDeps(o: {
  client: BackendApi;
  session: MerchantSession;
  /** Object URLs for bulk-scan thumbnails; tests run without a DOM. */
  previewUrl?: (file: File) => string;
  revokePreview?: (url: string) => void;
  /** The platform's Stripe publishable key, for paying in the app; none in tests. */
  stripePublishableKey?: string | null;
  /** The public site's origin (`VITE_HOBBYSHOP_URL`); hobbyshop.ai when unset. */
  publicSiteUrl?: string | null;
}): AppDeps {
  const { client, session } = o;
  const authHandler = createAuthHandler(session);
  const storeRepository = new StoreRepository();
  const reviewQueueRepository = new ReviewQueueRepository();

  const bulkScanQueue = new BulkScanQueue({
    storeRepository,
    previewUrl: o.previewUrl,
    revokePreview: o.revokePreview,
    scan: async ({ shopId, locationId }, images, onProgress, signal) => {
      const imageUrl = await fileToDataUrl(images.front);
      const backImageUrl = images.back ? await fileToDataUrl(images.back) : undefined;
      return runScan({
        client,
        session,
        shopId,
        locationId,
        images: { imageUrl, attemptId: images.attemptId, ...(backImageUrl ? { backImageUrl } : {}) },
        onProgress,
        signal,
      });
    },
    // A finished scan joins a group, and which group is the server's call, so
    // the queue is fetched again rather than spliced locally.
    onScanned: () => reviewQueueRepository.markStale(),
  });

  return {
    client,
    session,
    authHandler,
    profileRepository: new ProfileRepository(),
    storeRepository,
    locationsRepository: new LocationsRepository(),
    countriesRepository: new CountriesRepository(),
    pendingCartsRepository: new PendingCartsRepository(),
    providersRepository: new ProvidersRepository(),
    capabilitiesRepository: new CapabilitiesRepository(),
    reviewQueueRepository,
    customersRepository: new CustomersRepository(),
    bulkScanQueue,
    broadcastStudio: new BroadcastStudio({ client, session }),
    stripeTerminal: new StripeTerminal({ client, session }),
    stripePayments: new StripePayments(o.stripePublishableKey ?? null),
    shareLinks: new ShareLinks(o.publicSiteUrl),
  };
}
